import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaSearch,
    FaTable,
    FaCalendarAlt,
    FaChartBar,
    FaTimes,
    FaChartLine,
    FaClipboardList,
    FaCheckCircle,
    FaClock,
} from "react-icons/fa";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import "../PriceSurvyReports/PriceSurveyMatrixReportPage.css";
import "./ComplaintsMatrixReportPage.css";

const DATE_COLUMN = "day";
const DEFAULT_MAX_DAYS = 60;
const GOV_STATS_BAR_SIZE = 22;
const GOV_STATS_ROW_HEIGHT = 36;

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};

const isDateColumn = (col) => String(col).trim().toLowerCase() === DATE_COLUMN;

const formatDateLabel = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value ?? "");
    return date.toLocaleDateString("ar-SY", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const countDaysInclusive = (from, to) => {
    const start = new Date(from);
    const end = new Date(to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    return Math.floor((end - start) / 86400000) + 1;
};

const DayChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="ps-matrix-chart-tooltip">
            <div className="ps-matrix-chart-tooltip__location fw-bold mb-1">{label}</div>
            {payload.map((entry) => (
                <div key={entry.dataKey} className="small" style={{ color: entry.color }}>
                    {entry.name}: <strong>{entry.value}</strong>
                </div>
            ))}
        </div>
    );
};

const ComplaintCell = ({ processed, unprocessed }) => {
    const p = processed || 0;
    const u = unprocessed || 0;
    if (p === 0 && u === 0) {
        return <span className="cmatrix-cell-line cmatrix-cell-line--empty">—</span>;
    }
    return (
        <span
            className="cmatrix-cell-line"
            title={`معالجة: ${p} — معلّقة: ${u} — الإجمالي: ${p + u}`}
        >
            <span className="cmatrix-cell-line__p">{p}</span>
            <span className="cmatrix-cell-line__sep">/</span>
            <span className="cmatrix-cell-line__u">{u}</span>
        </span>
    );
};

const formatGovPeriodTotals = (gov, columnTotals) => {
    const t = columnTotals[gov] || { processed: 0, unprocessed: 0 };
    const total = t.processed + t.unprocessed;
    return { ...t, total };
};

const GovStatsTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload;
    if (!item) return null;
    return (
        <div className="ps-matrix-chart-tooltip">
            <div className="ps-matrix-chart-tooltip__location fw-bold mb-2">{item.governorate}</div>
            <div className="small text-success">معالجة: <strong>{item.processed}</strong></div>
            <div className="small text-danger">معلّقة: <strong>{item.unprocessed}</strong></div>
            <div className="small mt-1">الإجمالي: <strong>{item.total}</strong></div>
            <div className="small text-muted">نسبة المعالجة: {item.processingRate}%</div>
        </div>
    );
};

export default function ComplaintsMatrixReportPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { backEnd_request } = BackMethods();

    const [loading, setLoading] = useState(false);
    const [matrixData, setMatrixData] = useState(null);
    const [filters, setFilters] = useState({
        from: searchParams.get("from") || daysAgoIso(30),
        to: searchParams.get("to") || todayIso(),
    });
    const [rangeError, setRangeError] = useState("");
    const [dayChartModal, setDayChartModal] = useState(null);
    const [govChartModal, setGovChartModal] = useState(null);
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [govStatsOpen, setGovStatsOpen] = useState(false);

    const maxDays = matrixData?.maxDays || DEFAULT_MAX_DAYS;

    const governorateColumns = useMemo(() => {
        if (!matrixData?.columns) return [];
        return matrixData.columns.filter((col) => !isDateColumn(col));
    }, [matrixData]);

    const parsedRows = useMemo(() => {
        if (!matrixData?.rows?.length || !matrixData?.columns?.length) return [];

        const dayIdx = matrixData.columns.findIndex((col) => isDateColumn(col));
        if (dayIdx < 0) return [];

        return matrixData.rows.map((row) => {
            const rawDate = row[dayIdx];
            const dayIso = String(rawDate ?? "").slice(0, 10);
            const cells = {};
            matrixData.columns.forEach((col, colIdx) => {
                if (isDateColumn(col)) return;
                const cell = row[colIdx];
                cells[col] = {
                    processed: Number(cell?.processed) || 0,
                    unprocessed: Number(cell?.unprocessed) || 0,
                };
            });
            const totals = Object.values(cells).reduce(
                (acc, c) => ({
                    processed: acc.processed + c.processed,
                    unprocessed: acc.unprocessed + c.unprocessed,
                }),
                { processed: 0, unprocessed: 0 }
            );
            const hasData = totals.processed > 0 || totals.unprocessed > 0;
            return {
                date: rawDate,
                dayIso,
                dateLabel: formatDateLabel(rawDate),
                shortDate: new Date(dayIso).toLocaleDateString("ar-SY", {
                    day: "numeric",
                    month: "short",
                }),
                cells,
                totals,
                hasData,
            };
        });
    }, [matrixData, governorateColumns]);

    const summaryTotals = useMemo(() => {
        return parsedRows.reduce(
            (acc, row) => ({
                processed: acc.processed + row.totals.processed,
                unprocessed: acc.unprocessed + row.totals.unprocessed,
            }),
            { processed: 0, unprocessed: 0 }
        );
    }, [parsedRows]);

    const columnTotals = useMemo(() => {
        const totals = {};
        governorateColumns.forEach((gov) => {
            totals[gov] = { processed: 0, unprocessed: 0 };
        });
        parsedRows.forEach((row) => {
            governorateColumns.forEach((gov) => {
                const cell = row.cells[gov] || { processed: 0, unprocessed: 0 };
                totals[gov].processed += cell.processed;
                totals[gov].unprocessed += cell.unprocessed;
            });
        });
        return totals;
    }, [parsedRows, governorateColumns]);

    const governorateStats = useMemo(() => {
        return governorateColumns
            .map((gov) => {
                const t = columnTotals[gov] || { processed: 0, unprocessed: 0 };
                const total = t.processed + t.unprocessed;
                return {
                    governorate: gov,
                    processed: t.processed,
                    unprocessed: t.unprocessed,
                    total,
                    processingRate: total > 0 ? Math.round((t.processed / total) * 100) : 0,
                };
            })
            .sort((a, b) => b.total - a.total);
    }, [columnTotals, governorateColumns]);

    const govStatsChartData = useMemo(
        () => governorateStats.filter((g) => g.total > 0),
        [governorateStats]
    );

    const govStatsLabelWidth = useMemo(() => {
        if (!govStatsChartData.length) return 130;
        return Math.min(
            Math.max(...govStatsChartData.map((d) => d.governorate.length * 11), 130),
            240
        );
    }, [govStatsChartData]);

    const govStatsChartHeight = useMemo(
        () => Math.max(300, govStatsChartData.length * GOV_STATS_ROW_HEIGHT + 56),
        [govStatsChartData]
    );

    const grandTotal = summaryTotals.processed + summaryTotals.unprocessed;

    const timelineData = useMemo(
        () =>
            parsedRows.map((row) => ({
                dayIso: row.dayIso,
                shortDate: row.shortDate,
                dateLabel: row.dateLabel,
                processed: row.totals.processed,
                unprocessed: row.totals.unprocessed,
            })),
        [parsedRows]
    );

    const validateRange = useCallback(
        (from, to) => {
            if (!from || !to) return "حدّد تاريخ البداية والنهاية";
            if (from > to) return "تاريخ البداية يجب أن يكون قبل تاريخ النهاية";
            const days = countDaysInclusive(from, to);
            if (days > maxDays) return `الحد الأقصى للفترة ${maxDays} يوماً (المحدد: ${days})`;
            return "";
        },
        [maxDays]
    );

    const fetchReport = useCallback(
        async (override = null) => {
            const active = override ? { ...filters, ...override } : filters;
            const err = validateRange(active.from, active.to);
            setRangeError(err);
            if (err) return;

            setLoading(true);
            try {
                const res = await backEnd_request.get(
                    "/public/staff/complaints/matrix/governorates",
                    { params: { from: active.from, to: active.to } }
                );
                setMatrixData(res.data || null);
                if (override) setFilters(active);
            } catch (e) {
                console.error(e);
                setMatrixData(null);
            } finally {
                setLoading(false);
            }
        },
        [filters, backEnd_request, validateRange]
    );

    useEffect(() => {
        if (searchParams.get("autoLoad") === "1") {
            const from = searchParams.get("from") || filters.from;
            const to = searchParams.get("to") || filters.to;
            fetchReport({ from, to });
        }
    }, []);

    const handleDateChartClick = (row) => {
        const data = governorateColumns
            .map((gov) => ({
                governorate: gov,
                processed: row.cells[gov]?.processed || 0,
                unprocessed: row.cells[gov]?.unprocessed || 0,
            }))
            .filter((item) => item.processed > 0 || item.unprocessed > 0);

        setDayChartModal({
            dateLabel: row.dateLabel,
            dayIso: row.dayIso,
            data,
        });
    };

    const handleGovernorateChartClick = (gov) => {
        const data = parsedRows
            .map((row) => ({
                shortDate: row.shortDate,
                dateLabel: row.dateLabel,
                processed: row.cells[gov]?.processed || 0,
                unprocessed: row.cells[gov]?.unprocessed || 0,
            }))
            .filter((item) => item.processed > 0 || item.unprocessed > 0);

        setGovChartModal({ governorate: gov, data });
    };

    const returnToRegistry = () => {
        const params = new URLSearchParams({ tab: "all", page: "1", pageSize: "20" });
        if (filters.from) params.set("from", filters.from);
        if (filters.to) params.set("to", filters.to);
        navigate(`/admin/complaints-registry?${params.toString()}`);
    };

    const dateRangeLabel =
        matrixData?.from && matrixData?.to
            ? `${new Date(matrixData.from).toLocaleDateString("ar-SY")} — ${new Date(matrixData.to).toLocaleDateString("ar-SY")}`
            : "";

    const useScrollLayout = governorateColumns.length > 6;

    return (
        <div className="container-fluid px-3 px-md-4 py-4 ps-matrix-page cmatrix-page" dir="rtl">
            <div className="ps-matrix-header mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <div>
                    <h3>
                        <FaTable className="ms-2" />
                        تقرير الشكاوي — مصفوفة المحافظات
                    </h3>
                    <p className="mb-0 mt-1 opacity-75 text-white small">
                        {matrixData
                            ? "جدول إحصائي يومي لعدد الشكاوى المعالجة وغير المعالجة — استخدم أزرار التحليل البياني للرسوم التفصيلية"
                            : "حدّد المجال الزمني ثم اضغط عرض التقرير"}
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    {matrixData && (
                        <>
                            <span className="meta-badge">
                                <FaCalendarAlt className="ms-1" />
                                {dateRangeLabel}
                            </span>
                            <span className="meta-badge">
                                <FaCheckCircle className="ms-1" />
                                {summaryTotals.processed} معالجة
                            </span>
                            <span className="meta-badge">
                                <FaClock className="ms-1" />
                                {summaryTotals.unprocessed} معلّقة
                            </span>
                            <button
                                type="button"
                                className="btn btn-outline-light btn-sm fw-bold"
                                onClick={() => setGovStatsOpen(true)}
                            >
                                <FaChartBar className="ms-1" />
                                إحصاء المحافظات
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-light btn-sm fw-bold"
                                onClick={() => setTimelineOpen(true)}
                            >
                                <FaChartLine className="ms-1" />
                                نظرة زمنية
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        className="btn btn-light btn-sm fw-bold"
                        onClick={returnToRegistry}
                    >
                        <FaClipboardList className="ms-1" />
                        سجل الشكاوي
                    </button>
                </div>
            </div>

            <div className="ps-matrix-filters p-4 mb-3">
                <div className="row g-3 align-items-end">
                    <div className="col-6 col-md-3 col-lg-2">
                        <label className="text-danger">
                            <FaCalendarAlt className="ms-1" />
                            من تاريخ *
                        </label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.from}
                            max={filters.to || undefined}
                            onChange={(e) => {
                                setFilters((f) => ({ ...f, from: e.target.value }));
                                setRangeError("");
                            }}
                        />
                    </div>
                    <div className="col-6 col-md-3 col-lg-2">
                        <label className="text-danger">
                            <FaCalendarAlt className="ms-1" />
                            إلى تاريخ *
                        </label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.to}
                            min={filters.from || undefined}
                            onChange={(e) => {
                                setFilters((f) => ({ ...f, to: e.target.value }));
                                setRangeError("");
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                        <button
                            type="button"
                            className="btn btn-primary w-100 fw-bold"
                            style={{ height: "42px" }}
                            disabled={loading}
                            onClick={() => fetchReport()}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                <>
                                    <FaSearch className="ms-2" />
                                    عرض التقرير
                                </>
                            )}
                        </button>
                    </div>
                    <div className="col-12 col-lg-5">
                        <div className="cmatrix-legend small text-muted">
                            <span>كل خلية: <strong>معالجة / معلّقة</strong> — مرّر المؤشر على اسم المحافظة لعرض مجموع الفترة</span>
                            <span className="text-secondary">| الحد الأقصى: {maxDays} يوماً</span>
                        </div>
                    </div>
                </div>
                {rangeError && (
                    <div className="alert alert-danger py-2 px-3 mt-3 mb-0 small">{rangeError}</div>
                )}
            </div>

            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                </div>
            )}

            {!loading && matrixData?.rows?.length > 0 && (
                <div className="ps-matrix-table-wrap">
                    {useScrollLayout && (
                        <div className="ps-matrix-scroll-hint">
                            ← مرّر أفقياً لعرض جميع المحافظات →
                        </div>
                    )}
                    <div className={`ps-matrix-scroll ${useScrollLayout ? "ps-matrix-scroll--wide" : ""}`}>
                        <table className={`ps-matrix-table cmatrix-table ${useScrollLayout ? "ps-matrix-table--scroll" : ""}`}>
                            <colgroup>
                                <col className="col-product-width" />
                                {governorateColumns.map((col) => (
                                    <col key={col} className="col-price-width" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="col-product">التاريخ</th>
                                    {governorateColumns.map((col) => {
                                        const period = formatGovPeriodTotals(col, columnTotals);
                                        return (
                                            <th
                                                key={col}
                                                className="col-drilldown cmatrix-th-wrap"
                                                onClick={() => handleGovernorateChartClick(col)}
                                                style={
                                                    useScrollLayout
                                                        ? {
                                                              minWidth: `${Math.min(Math.max(col.length * 8, 72), 120)}px`,
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <span className="cmatrix-th-label">{col}</span>
                                                <div className="cmatrix-th-popover">
                                                    <div className="cmatrix-th-popover__title">مجموع الفترة</div>
                                                    <div className="cmatrix-th-popover__row">
                                                        <span>معالجة</span>
                                                        <strong className="text-success">{period.processed}</strong>
                                                    </div>
                                                    <div className="cmatrix-th-popover__row">
                                                        <span>معلّقة</span>
                                                        <strong className="text-danger">{period.unprocessed}</strong>
                                                    </div>
                                                    <div className="cmatrix-th-popover__row cmatrix-th-popover__row--sum">
                                                        <span>الإجمالي</span>
                                                        <strong>{period.total}</strong>
                                                    </div>
                                                    <div className="cmatrix-th-popover__hint">اضغط لعرض السجل الزمني</div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {parsedRows.map((row, idx) => (
                                    <tr
                                        key={`${row.dayIso}-${idx}`}
                                        className={!row.hasData ? "ps-matrix-row--no-survey" : ""}
                                    >
                                        <td
                                            className="col-product col-product-chart"
                                            title="اضغط لعرض الرسم البياني لهذا اليوم"
                                            onClick={() => handleDateChartClick(row)}
                                        >
                                            {row.dateLabel}
                                        </td>
                                        {governorateColumns.map((gov) => {
                                            const cell = row.cells[gov] || { processed: 0, unprocessed: 0 };
                                            return (
                                                <td key={gov} className="cmatrix-td">
                                                    <ComplaintCell
                                                        processed={cell.processed}
                                                        unprocessed={cell.unprocessed}
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && !matrixData && (
                <div className="ps-matrix-empty">
                    <div className="icon">
                        <FaTable />
                    </div>
                    <h5 className="fw-bold text-secondary">ابدأ بتحديد المجال الزمني</h5>
                    <p className="text-muted small mb-0">اختر تاريخ البداية والنهاية ثم اضغط «عرض التقرير»</p>
                </div>
            )}

            {!loading && matrixData && (!matrixData.rows || matrixData.rows.length === 0) && (
                <div className="ps-matrix-empty">
                    <div className="icon">
                        <FaSearch />
                    </div>
                    <h5 className="fw-bold text-secondary">لا توجد بيانات في هذا المجال</h5>
                </div>
            )}

            {dayChartModal && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setDayChartModal(null)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-chart-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaChartBar className="text-primary" />
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold">توزيع الشكاوي — {dayChartModal.dateLabel}</h6>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setDayChartModal(null)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-chart-modal__body">
                            {dayChartModal.data.length > 0 ? (
                                <div
                                    className="ps-matrix-chart-wrap"
                                    style={{ height: Math.max(320, dayChartModal.data.length * 42) }}
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={dayChartModal.data}
                                            layout="vertical"
                                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                            <XAxis type="number" allowDecimals={false} />
                                            <YAxis
                                                type="category"
                                                dataKey="governorate"
                                                width={Math.min(
                                                    160,
                                                    Math.max(
                                                        ...dayChartModal.data.map((d) => d.governorate.length * 9),
                                                        100
                                                    )
                                                )}
                                                tick={{ fontSize: 11 }}
                                            />
                                            <Tooltip content={<DayChartTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="processed"
                                                name="معالجة"
                                                stackId="a"
                                                fill="#198754"
                                                radius={[0, 0, 0, 0]}
                                            />
                                            <Bar
                                                dataKey="unprocessed"
                                                name="معلّقة"
                                                stackId="a"
                                                fill="#dc3545"
                                                radius={[0, 4, 4, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="alert alert-secondary text-center small mb-0">
                                    لا توجد شكاوى في هذا اليوم
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {govChartModal && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setGovChartModal(null)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-timeline-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaChartLine className="text-primary" />
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold">السجل الزمني — {govChartModal.governorate}</h6>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setGovChartModal(null)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-timeline-modal__body">
                            {govChartModal.data.length > 0 ? (
                                <div className="ps-timeline-chart-wrap" style={{ height: 360 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={govChartModal.data}
                                            margin={{ top: 16, right: 24, left: 4, bottom: 48 }}
                                        >
                                            <CartesianGrid strokeDasharray="4 4" />
                                            <XAxis
                                                dataKey="shortDate"
                                                tick={{ fontSize: 10 }}
                                                angle={-35}
                                                textAnchor="end"
                                                height={56}
                                            />
                                            <YAxis allowDecimals={false} width={40} />
                                            <Tooltip
                                                labelFormatter={(_, payload) =>
                                                    payload?.[0]?.payload?.dateLabel || ""
                                                }
                                            />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="processed"
                                                name="معالجة"
                                                stroke="#198754"
                                                strokeWidth={2.5}
                                                dot={{ r: 3 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="unprocessed"
                                                name="معلّقة"
                                                stroke="#dc3545"
                                                strokeWidth={2.5}
                                                dot={{ r: 3 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div className="alert alert-secondary text-center small mb-0">
                                    لا توجد بيانات لهذه المحافظة في الفترة المحددة
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {govStatsOpen && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setGovStatsOpen(false)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-timeline-modal cmatrix-stats-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaChartBar className="text-primary" />
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold">إحصاء المحافظات — ترتيب الشكاوي</h6>
                                <small className="text-muted">{dateRangeLabel}</small>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setGovStatsOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-timeline-modal__body">
                            <div
                                className="cmatrix-inline-chart cmatrix-gov-chart mb-3"
                                style={{ height: govStatsChartHeight }}
                            >
                                <div
                                    className="cmatrix-gov-chart__labels"
                                    style={{ width: govStatsLabelWidth }}
                                    aria-hidden="true"
                                >
                                    {govStatsChartData.map((row) => (
                                        <div key={row.governorate} className="cmatrix-gov-chart__label">
                                            <span>{row.governorate}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="cmatrix-gov-chart__plot">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={govStatsChartData}
                                            layout="vertical"
                                            margin={{ top: 12, right: 16, left: 8, bottom: 32 }}
                                            barSize={GOV_STATS_BAR_SIZE}
                                            barCategoryGap="18%"
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                            <YAxis type="category" dataKey="governorate" hide />
                                            <Tooltip content={<GovStatsTooltip />} />
                                            <Legend />
                                            <Bar dataKey="processed" name="معالجة" stackId="gov" fill="#198754" />
                                            <Bar
                                                dataKey="unprocessed"
                                                name="معلّقة"
                                                stackId="gov"
                                                fill="#dc3545"
                                                radius={[0, 4, 4, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-0 cmatrix-ranking-table text-center">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>المحافظة</th>
                                            <th className="text-success">معالجة</th>
                                            <th className="text-danger">معلّقة</th>
                                            <th>الإجمالي</th>
                                            <th>نسبة المعالجة</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {governorateStats.map((row, idx) => (
                                            <tr key={row.governorate} className={row.total === 0 ? "text-muted" : ""}>
                                                <td className="fw-bold">{idx + 1}</td>
                                                <td className="fw-semibold text-end">{row.governorate}</td>
                                                <td className="text-success fw-bold">{row.processed}</td>
                                                <td className="text-danger fw-bold">{row.unprocessed}</td>
                                                <td className="fw-bold">{row.total}</td>
                                                <td>
                                                    <span className={`badge ${row.processingRate >= 80 ? "bg-success" : row.processingRate >= 50 ? "bg-warning text-dark" : "bg-danger"}`}>
                                                        {row.processingRate}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="table-secondary fw-bold">
                                            <td colSpan={2} className="text-end">المجموع الكلي</td>
                                            <td className="text-success">{summaryTotals.processed}</td>
                                            <td className="text-danger">{summaryTotals.unprocessed}</td>
                                            <td>{grandTotal}</td>
                                            <td>
                                                {grandTotal > 0
                                                    ? `${Math.round((summaryTotals.processed / grandTotal) * 100)}%`
                                                    : "—"}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {timelineOpen && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setTimelineOpen(false)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-timeline-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaChartLine className="text-primary" />
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold">النظرة الزمنية الإجمالية</h6>
                                <small className="text-muted">مجموع كل المحافظات يومياً</small>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setTimelineOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-timeline-modal__body">
                            <div className="ps-timeline-chart-wrap" style={{ height: 380 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={timelineData}
                                        margin={{ top: 16, right: 24, left: 4, bottom: 48 }}
                                    >
                                        <CartesianGrid strokeDasharray="4 4" />
                                        <XAxis
                                            dataKey="shortDate"
                                            tick={{ fontSize: 10 }}
                                            interval={timelineData.length > 20 ? 2 : 0}
                                            angle={-35}
                                            textAnchor="end"
                                            height={56}
                                        />
                                        <YAxis allowDecimals={false} width={40} />
                                        <Tooltip
                                            labelFormatter={(_, payload) =>
                                                payload?.[0]?.payload?.dateLabel || ""
                                            }
                                        />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="processed"
                                            name="معالجة"
                                            stroke="#198754"
                                            strokeWidth={2.5}
                                            dot={false}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="unprocessed"
                                            name="معلّقة"
                                            stroke="#dc3545"
                                            strokeWidth={2.5}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
